import { el, btn, menuCard, MENU_ICONS } from '@/ui/dom';
import { brandLogo } from '@/ui/brandLogo';
import { sfxUiTick } from '@/audio/sfx';
import type { SaveData } from '@/core/SaveData';
import {
  isTourComplete,
  tourCityIndex,
  tourCityAt,
  sanitizeNickname,
  NICKNAME_MAX,
} from '@/core/SaveData';
import { CITIES } from '@/config/cities';
import { skillTier, nextSkillTier, spBalance, SKILL_TIERS } from '@/core/skillPoints';
import { SP_COIN_SVG, XP_STAR_SVG } from '@/ui/currencyIcons';
import { localLeaderboard } from '@/core/leaderboard';
import { LEADERBOARD_ENABLED } from '@/config/features';
import { citySilhouette } from '@/ui/citySilhouettes';
import { cssColor } from '@/ui/format';
import { paintTitleBackdrop } from '@/ui/title/titleBackdrop';
import { span } from '@/dev/perf';
import { TitleStage } from '@/ui/title/TitleStage';
import { BUILD_TAG } from '@/config/version';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';
import { BUY_GAME_LABEL, CROWN_SVG, CHEV_SVG } from '@/ui/lockCta';

/**
 * TitleScreen.ts — the flagship front door, redesigned in M80 to the owner's
 * stadium reference: a painted stadium-night backdrop under a live 3D stage
 * (the WORLD TOUR CUP trophy with the globe seated in its cradle front-left,
 * the big tour-route globe rolling behind) with the UI floating over it —
 * profile block top-left (nickname · skill tier · progress bar · world
 * rank; signed in, it is one button into the Racer ID dashboard), the
 * chrome/gold logo, a quick-stats column, and the menu as a hero tour card +
 * option cards down the right.
 *
 * FIRST arrival each page load plays the full intro (the globe bounces its
 * way into the cup while the UI staggers in); returning from other screens
 * gets only the subtle idle (cup rotating) + a quick fade.
 *
 * The old live-track flythrough no longer shows here — Flow pauses the
 * attract render while this screen is up (onVisibleChange).
 */
export interface TitleCallbacks {
  onStartTour: () => void; // also "restart" once confirmed
  onContinueTour: () => void;
  onQuickRace: () => void;
  onPlayOnline: () => void;
  onGarage: () => void;
  onLeaderboard: () => void;
  onSettings: () => void;
  /** M88 — the title speaker button: flip the persisted mute, return the NEW
   *  state so the button repaints itself. */
  onToggleMute: () => boolean;
  /** M102 — the ⏭ button beside mute: skip the menu playlist to its next
   *  track (no-op when file music isn't what's playing). */
  onNextTrack: () => void;
  /** M86b — the HOW TO PLAY guide (the card at the bottom of the menu). */
  onHowToPlay: () => void;
  /** M114 — the SHARE card under HOW TO PLAY: the game link (or the player's
   *  referral link once signed in) into the native share sheet. */
  onShare: () => void;
  /** M80 — the player renamed themselves (already sanitized). Guests only:
   *  a SIGNED-IN player's username is renamed on the Racer ID dashboard
   *  (owner round: the title module just opens it — no inline editor). */
  onNickname: (name: string) => void;
  /** M100 — open the RACER ID screen (create / manage the account). Also the
   *  tap target of the WHOLE signed-in profile module. */
  onAccount: () => void;
  /** Home-modernize round: the top-right LOG IN button (the account screen in
   *  its sign-in voice). Falls back to onAccount when not wired. */
  onLogIn?: () => void;
  /** M123 — the GET THE FULL GAME strip (non-owners only): open the paywall. */
  onGetGame?: () => void;
  /** M101b — open the owner-only Admin Dashboard (is_admin accounts). */
  onAdmin?: () => void;
  /** M80 — Flow pauses the world attract render while the title covers it. */
  onVisibleChange: (visible: boolean) => void;
}

/** M100 — what the title needs to know about the account layer. */
export interface TitleAccountInfo {
  status: 'disabled' | 'loading' | 'signedOut' | 'signedIn';
  /** World rank from the server (signed in + synced), else null. */
  rank: number | null;
  /** Signed-out earnings waiting in the guest pot. */
  guestPoints: number;
  /** M101b — the server-side admin flag (shows the dashboard entry). */
  isAdmin?: boolean;
  /** M103 — the account's SERVER-side username. Signed in, this is the
   *  identity (it beats the device-local nickname, which can be stale on a
   *  shared or roamed device); null until the first wallet pull answers. */
  nickname?: string | null;
  /**
   * M123 — the STOREFRONT tri-state, derived upstream (Flow.titleOwnership):
   *  false = a RESOLVED non-owner — show the GET THE FULL GAME strip and the
   *          try-it-free Quick Race sub.
   *  true  = an owner (or a build with no paywall) — byte-identical title.
   *  null / absent = UNRESOLVED (account loading, wallet silent) — ALSO the
   *          unchanged title: never advertise at a possible owner.
   */
  ownsGame?: boolean | null;
  /** M123 — the formatted display price for the strip (e.g. "$9.99"). */
  gamePrice?: string;
}

const RESET_CONFIRM_SECONDS = 3;

/**
 * M115 (owner: the SHARE card said "CHALLENGE YOUR FRIENDS" instead of an
 * earn-SP message). Confirmed in Flow.shareGame(): a SIGNED-IN player shares
 * their referral link and really does earn SP — 20 per friend who creates a
 * Racer ID, capped at 5 friends / 100 SP, all enforced server-side. A GUEST
 * has no referral code yet, so they share a plain game link and earn nothing —
 * promising them SP would be a lie, so that state gets an honest nudge toward
 * creating a Racer ID instead.
 */
function shareSub(acct: { status: string } | null): string {
  return acct?.status === 'signedIn'
    ? 'EARN 20 XP & SP PER FRIEND WHO JOINS'
    : 'EARN XP & SP · NEEDS A RACER ID';
}

/** M123 — the QUICK RACE card sells the free teaser to a RESOLVED non-owner
 *  only (strict `=== false`: null/loading keeps today's copy, so a possible
 *  owner never sees storefront language). M147 (owner): for every non-owner
 *  (signed in or not — the tap plays the demo for both) the card itself is
 *  DEMO RACE, "TRY THE GAME FOR FREE!". */
function quickRaceTitle(acct: TitleAccountInfo | null): string {
  return acct?.ownsGame === false ? 'DEMO RACE' : 'QUICK RACE';
}
function quickRaceSub(acct: TitleAccountInfo | null): string {
  return acct?.ownsGame === false ? 'TRY THE GAME FOR FREE!' : 'JUMP IN & PLAY';
}

// ---- Icon set (local to the title's new chrome) ----
const HELMET_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 3.2c-4.9 0-8.4 3.6-8.4 8.3 0 1.6.4 3 .9 4l-.7 3 3.2-.7c.6.3 1.4.5 2.1.5h8.6c1.5 0 2.7-1.2 2.7-2.7v-4.1c0-4.7-3.5-8.3-8.4-8.3Zm6.3 8.1h-5.6c-1 0-1.7.8-1.7 1.7 0 1 .8 1.7 1.7 1.7h5.5v.9c0 .4-.3.7-.7.7h-5c-2 0-3.6-1.5-3.6-3.4 0-1.9 1.6-3.4 3.6-3.4h5.5c.2.55.3 1.15.3 1.8Z"/></svg>';
const PENCIL_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="m16.9 4.1 3 3L8.6 18.4l-3.9.9.9-3.9L16.9 4.1Zm1.4-1.4 1.2-1.2a1.4 1.4 0 0 1 2 0l1 1a1.4 1.4 0 0 1 0 2l-1.2 1.2-3-3Z" transform="translate(-1 1)"/></svg>';
const HELP_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 2.6a9.4 9.4 0 1 0 0 18.8 9.4 9.4 0 0 0 0-18.8Zm0 2a7.4 7.4 0 1 1 0 14.8 7.4 7.4 0 0 1 0-14.8Z"/>' +
  '<path d="M12 7.1c-1.9 0-3.2 1-3.5 2.7l1.9.4c.2-.8.8-1.3 1.6-1.3.8 0 1.3.5 1.3 1.1 0 .5-.2.8-1 1.4-.9.7-1.4 1.4-1.4 2.4v.5h2v-.4c0-.5.2-.9 1-1.5.9-.7 1.5-1.4 1.5-2.5 0-1.7-1.4-2.8-3.4-2.8ZM11 15.9h2.1V18H11z"/></svg>';
const GEAR_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 8.6a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8Zm8.9 5.1-1.9 3.3-2.3-.5a7 7 0 0 1-1.6.9l-.7 2.3h-3.8l-.7-2.3a7 7 0 0 1-1.6-.9l-2.3.5-1.9-3.3 1.6-1.7a7.3 7.3 0 0 1 0-1.9L4.1 8.3 6 5l2.3.5a7 7 0 0 1 1.6-.9l.7-2.3h3.8l.7 2.3c.6.2 1.1.5 1.6.9L19 5l1.9 3.3-1.6 1.7c.1.6.1 1.3 0 1.9l1.6 1.8Z"/></svg>';
// M88 — the mute toggle's two faces (speaker with waves / speaker with an X).
// Exported (M101b): the onboarding flow shows the same mute toggle.
export const SPEAKER_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M4 9.2v5.6h3.6l4.9 4.2V5L7.6 9.2H4Z"/>' +
  '<path d="M15.9 8.9a4.4 4.4 0 0 1 0 6.2l-1.3-1.3a2.6 2.6 0 0 0 0-3.6l1.3-1.3Z"/>' +
  '<path d="M18.4 6.4a7.9 7.9 0 0 1 0 11.2L17 16.2a6 6 0 0 0 0-8.4l1.4-1.4Z"/></svg>';
export const SPEAKER_MUTED_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M4 9.2v5.6h3.6l4.9 4.2V5L7.6 9.2H4Z"/>' +
  '<path d="m16 9.2 1.4-1.4 2.1 2.1 2.1-2.1L23 9.2l-2.1 2.1L23 13.4 21.6 14.8l-2.1-2.1-2.1 2.1-1.4-1.4 2.1-2.1L16 9.2Z"/></svg>';
// M114 — the SHARE card's glyph (the standard share-nodes mark).
const SHARE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="17.6" cy="5.6" r="2.9"/><circle cx="6.4" cy="12" r="2.9"/>' +
  '<circle cx="17.6" cy="18.4" r="2.9"/>' +
  '<path d="m8.9 10.6 6.1-3.5 1 1.7-6.1 3.5zM8.9 13.4l6.1 3.5 1-1.7-6.1-3.5z"/></svg>';
// M102 — the ⏭ NEXT TRACK button (standard skip glyph: two triangles + bar).
const NEXT_TRACK_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M5 6.2 12 12 5 17.8V6.2Z"/>' +
  '<path d="M12.4 6.2 19.4 12l-7 5.8V6.2Z"/>' +
  '<path d="M19 6h2v12h-2z"/></svg>';
// Owner reference (2026-10-02): the buy pill wears a crown, LOG IN a person.
const PERSON_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="12" cy="7.6" r="4.2"/>' +
  '<path d="M3.8 20.6c.5-4.3 3.9-7 8.2-7s7.7 2.7 8.2 7H3.8Z"/></svg>';
/** Per-card background art (owner reference: each menu button carries a faint
 *  scene on its right side). Existing swappable game art, tinted by CSS. */
const CARD_ART: Record<string, string> = {
  'QUICK RACE': 'online/bg.webp',
  'PLAY ONLINE': 'lobby/bg.webp',
  GARAGE: 'cards/veh_sports.webp',
};
/** 2450 → "2,450". */
function fmt(n: number): string {
  return n.toLocaleString('en-US');
}

export class TitleScreen {
  private root: HTMLDivElement;
  private ui: HTMLDivElement;
  private stage: TitleStage;
  private confirmTimer: number | null = null;
  private editing = false;
  private visible = false;
  /** M100 — the account snapshot the current UI was built with. */
  private account: TitleAccountInfo | null = null;
  /** M102c — the account-dependent corner + ghost row, swapped in place by
   *  refreshAccount() so a sync repaint never rebuilds the whole screen. */
  private profileEl: HTMLElement | null = null;
  /** M115 — the SHARE menu card, so its sub can be re-worded in place once the
   *  account status resolves (signed in = a real earn-SP promise). */
  private shareCard: HTMLElement | null = null;
  /** M123 — the QUICK RACE card, same in-place re-wording pattern: ownership
   *  resolves ~a second after boot, and the sub flips to the try-it-free line
   *  for a resolved non-owner without re-running show(). */
  private quickCard: HTMLElement | null = null;
  private ghostRow: HTMLElement | null = null;
  /** Owner round (home modernize): the top-right LOG IN button — shown only
   *  to a RESOLVED signed-out web player (never Big Kid), toggled in place by
   *  refreshAccount so a late-resolving account never rebuilds the screen. */
  private loginBtn: HTMLButtonElement | null = null;
  /** The cards that wear the storefront badges (FREE / FULL GAME), repainted
   *  in place when ownership resolves. */
  private bigkid = false;
  private unsubViewport: ViewportUnsubscribe | null = null;
  private readonly onResize = (): void => {
    if (this.visible) this.stage.resize();
  };

  constructor(
    parent: HTMLElement,
    private cb: TitleCallbacks,
  ) {
    this.root = el('div', 'screen screen-title', parent);
    this.root.style.display = 'none';

    // Layer 0/1: the painted stadium night + the optional generated-art
    // drop-in (public/title/bg.webp replaces the canvas when present).
    const bd = span('title.backdrop', () => paintTitleBackdrop());
    bd.className = 'title-bd';
    this.root.appendChild(bd);
    const art = document.createElement('img');
    art.className = 'title-bd-img';
    art.alt = '';
    art.addEventListener('error', () => art.remove());
    art.src = `${import.meta.env.BASE_URL}title/bg.webp`;
    this.root.appendChild(art);
    el('div', 'title-bd-grade', this.root);

    // Layer 2: the 3D stage (trophy + globes) — appends its own canvas.
    this.stage = span('title.stage', () => new TitleStage(this.root));

    // Layer 3: the UI, rebuilt per show().
    this.ui = el('div', 'title-ui', this.root);

    this.unsubViewport = subscribeViewport(this.onResize);
  }

  get isVisible(): boolean {
    return this.visible;
  }

  /** Where the Racer ID profile block sits on screen (the post-login welcome
   *  bubble parks under it, tail pointing up at the name). */
  profileRect(): DOMRect | null {
    const block = this.ui.querySelector('.title-profile');
    return block ? block.getBoundingClientRect() : null;
  }

  /** (Re)build the UI for the current save and open the stage. M102c (owner
   *  rule): EVERY arrival at the home page plays the full bounce-in landing
   *  (reduced motion still seats instantly). Account-state repaints while
   *  the title is already up go through refreshAccount() instead — a full
   *  show() mid-bounce used to cancel the animation and blink the UI. */
  show(save: SaveData, account: TitleAccountInfo | null = null): void {
    this.clearConfirm();
    this.editing = false;
    this.account = account;
    this.buildUi(save);

    const reduced = save.settings.reducedMotion;
    const intro = !reduced;
    // The logo's shine sweep + the hero sheen park under the game's own
    // reduced-motion setting too (the CSS media query covers the OS one).
    this.root.classList.toggle('reduced', reduced);
    this.root.classList.remove('intro', 'warm');
    // Reflow so re-adding the class restarts the entrance animations.
    void this.root.offsetWidth;
    this.root.classList.add(intro ? 'intro' : 'warm');

    this.root.style.display = '';
    this.visible = true;
    this.stage.setReducedMotion(reduced);
    span('title.stageShow', () => this.stage.show({ intro }));
    this.stage.resize(); // the host was display:none at construction
    this.cb.onVisibleChange(true);
  }

  // ---- UI assembly ---------------------------------------------------------

  private buildUi(save: SaveData): void {
    this.ui.replaceChildren();

    // Top bar: profile (left) · settings (right). The leaderboard (when
    // LEADERBOARD_ENABLED) opens from the profile block — a second icon for it
    // read as clutter (owner note); with the board hidden the block opens the
    // RACER ID screen instead.
    const top = el('div', 'title-top', this.ui);
    this.profileEl = this.buildProfile(save);
    top.appendChild(this.profileEl);
    const btns = el('div', 'title-topbtns', top);
    // M88 (owner): a one-tap MUTE beside SETTINGS, and SETTINGS wears its name
    // now — the menu-card glass language, just smaller.
    // M102 (owner): a ⏭ NEXT TRACK beside it — skip the menu playlist's song.
    // Reuses the mute button's square styling class (same size family).
    const nextBtn = btn('', 'title-topbtn title-mutebtn title-nextbtn', btns, () =>
      this.cb.onNextTrack(),
    );
    nextBtn.innerHTML = NEXT_TRACK_SVG;
    nextBtn.setAttribute('aria-label', 'Next song');
    nextBtn.title = 'Next song';
    const muteBtn = btn('', 'title-topbtn title-mutebtn', btns, () => {
      this.paintMute(muteBtn, this.cb.onToggleMute());
    });
    this.paintMute(muteBtn, save.settings.muted);
    const settingsBtn = btn('', 'title-topbtn', btns, () => this.cb.onSettings());
    settingsBtn.innerHTML = `${GEAR_SVG}<span class="title-topbtn-label">SETTINGS</span>`;
    settingsBtn.setAttribute('aria-label', 'Settings');
    settingsBtn.title = 'Settings';
    // Owner round (home modernize): signed out, the Racer ID entry is a LOG IN
    // button in this same top-right button language (the corner it used to
    // share with CREATE RACER ID now sells the full game). Gold-outlined: it
    // is an affirmative action (the M92 colour rule), and the one a returning
    // racer on a new device is looking for. Same AccountScreen as before —
    // Google / Apple / email serve sign-up AND sign-in.
    this.bigkid = save.settings.difficulty === 'bigkid';
    this.loginBtn = btn('', 'title-topbtn title-loginbtn', btns, () =>
      (this.cb.onLogIn ?? this.cb.onAccount)(),
    );
    this.loginBtn.innerHTML = `${PERSON_SVG}<span class="title-topbtn-label">LOG IN</span><span class="title-topbtn-chev">${CHEV_SVG}</span>`;
    this.loginBtn.setAttribute('aria-label', 'Log in to your Racer ID');
    this.paintLogin();

    // Left column: brand over the trophy, quick stats at the bottom.
    const left = el('div', 'title-left', this.ui);
    const brand = el('div', 'title-brand', left);
    // M148: the owner's logo art (WORLD TOUR CUP is part of the image), with
    // the shine travelling along its swoops.
    brandLogo(brand, 'title-brandlogo');

    // Right column: the hero tour card + the option cards.
    const menu = el('div', 'title-menu', this.ui);
    menu.appendChild(this.buildHeroCard(save));
    // M83 BIG KID: online play is OFF at the kids difficulty. M115 (owner):
    // the card is now HIDDEN outright rather than shown locked — a kid should
    // not see a door they can't open. It returns the moment any other
    // difficulty is selected (Settings re-shows the title, which rebuilds
    // these cards from the live save). The real guards are unchanged:
    // Flow.showOnlineHub still refuses, and an invite link still lands on the
    // title instead of joining.
    const kidLocked = save.settings.difficulty === 'bigkid';
    const cards: [string, string, string, string, boolean, () => void][] = [
      [
        MENU_ICONS.bolt,
        'QUICK RACE',
        // M123: a resolved non-owner is sold the free teaser here.
        quickRaceSub(this.account),
        '#38e1ff',
        false,
        () => this.cb.onQuickRace(),
      ],
      ...(kidLocked
        ? []
        : ([
            [
              MENU_ICONS.people,
              'PLAY ONLINE',
              'RACE REAL PLAYERS',
              '#b07cff',
              false,
              () => this.cb.onPlayOnline(),
            ],
          ] as [string, string, string, string, boolean, () => void][])),
      [MENU_ICONS.car, 'GARAGE', 'CHOOSE YOUR RIDE', '#2dd4bf', false, () => this.cb.onGarage()],
      // M86b (owner): HOW TO PLAY lives at the very bottom of the main menu
      // as a full titled card (the old top-right ? icon is gone).
      [
        HELP_SVG,
        'HOW TO PLAY',
        'LEARN THE GAME IN 60 SECONDS',
        '#ffd34a',
        false,
        () => this.cb.onHowToPlay(),
      ],
    ];
    // M114 (owner): SHARE at the very bottom, under HOW TO PLAY — the game
    // link (or your referral link once signed in) into the share sheet. Not
    // rendered in Big Kid (M83: no share/account CTAs for kids).
    if (!kidLocked) {
      cards.push([
        SHARE_SVG,
        'SHARE',
        shareSub(this.account),
        '#38e1ff',
        false,
        () => this.cb.onShare(),
      ]);
    }
    cards.forEach(([icon, title, sub, accent, locked, fn], i) => {
      const card: HTMLButtonElement = menuCard(
        { icon, title, sub, accent },
        menu,
        locked
          ? () => {
              card.classList.remove('shake');
              void card.offsetWidth; // restart the animation on repeat taps
              card.classList.add('shake');
            }
          : fn,
      );
      if (locked) card.classList.add('mcard-locked');
      // M115: the SHARE sub depends on whether you are signed in (only a Racer
      // ID earns SP), and the account resolves ~a second AFTER boot — so keep
      // a handle and let refreshAccount re-word it in place (M102c rule: never
      // re-run show(), it cancels the globe bounce-in).
      if (title === 'SHARE') this.shareCard = card;
      // M123: same pattern for the QUICK RACE card (ownership resolves late);
      // M147: its TITLE flips too (DEMO RACE for a non-owner).
      if (title === 'QUICK RACE') {
        this.quickCard = card;
        this.paintQuickCard();
      }
      card.style.setProperty('--stag', String(i + 1));
      // Owner reference: a faint scene on the card's right side (a CSS
      // background, so a missing file simply shows the tinted glass).
      const art = CARD_ART[title];
      if (art) {
        const bg = el('span', 'mcard-art', card);
        bg.style.backgroundImage = `url("${import.meta.env.BASE_URL}${art}")`;
        card.insertBefore(bg, card.firstChild);
      }
    });

    this.ghostRow = el('div', 'title-ghostrow', menu);
    this.fillGhostRow(save);
    // M91: the build tag makes "which version is this phone actually running?"
    // answerable at a glance (stale-SW shells used to masquerade as bugs).
    el('div', 'title-footer', menu).textContent = `v0.4 — World Tour · ${BUILD_TAG}`;
  }

  /** The quiet bottom row: RESTART TOUR (mid-tour) + the admin entry
   *  (M101b, server flag). Factored so refreshAccount can rebuild it. */
  private fillGhostRow(save: SaveData): void {
    if (!this.ghostRow) return;
    this.ghostRow.replaceChildren();
    const midTour = !!save.tour && !isTourComplete(save) && save.tour.results.length > 0;
    if (midTour) this.addRestartButton('RESTART TOUR', this.ghostRow);
    if (this.account?.isAdmin) {
      btn('ADMIN DASHBOARD', 'btn btn-ghost', this.ghostRow, () => this.cb.onAdmin?.());
    }
  }

  /** M102c — account-state repaint while the title is UP: swap only the
   *  account corner + ghost row IN PLACE. Never restarts the entrance or
   *  touches the stage — a full show() here used to cancel the bounce-in
   *  mid-flight (the stage saw "not seated" and snapped the ball into the
   *  cup) and re-run every entrance animation (the blink). */
  refreshAccount(save: SaveData, account: TitleAccountInfo | null): void {
    if (!this.visible || this.editing) return;
    this.account = account;
    this.clearConfirm();
    const fresh = this.buildProfile(save);
    this.profileEl?.replaceWith(fresh);
    this.profileEl = fresh;
    this.fillGhostRow(save);
    // M115: re-word the SHARE card now the account status is known.
    const sub = this.shareCard?.querySelector('.mcard-sub');
    if (sub) sub.textContent = shareSub(account);
    // M123: and the QUICK RACE card, now ownership is known.
    this.paintQuickCard();
    this.paintLogin();
  }

  /** M147 — QUICK RACE / DEMO RACE: the card's title + sub from ownership. */
  private paintQuickCard(): void {
    const t = this.quickCard?.querySelector('.mcard-title');
    if (t) t.textContent = quickRaceTitle(this.account);
    const sub = this.quickCard?.querySelector('.mcard-sub');
    if (sub) sub.textContent = quickRaceSub(this.account);
  }

  /** LOG IN shows only for a RESOLVED signed-out player (never 'loading' —
   *  it would flash at a signed-in player every boot), never in Big Kid
   *  (M83: no account CTAs for kids). */
  private paintLogin(): void {
    if (!this.loginBtn) return;
    const show = this.account?.status === 'signedOut' && !this.bigkid;
    this.loginBtn.style.display = show ? '' : 'none';
  }

  /** The profile block: helmet avatar · nickname · tier chip + SP ·
   *  progress bar toward the next tier · world rank. Tapping it opens the
   *  leaderboard (except the name, which edits).
   *  Owner round (Racer ID dashboard): SIGNED IN, the whole block is ONE
   *  button into the Racer ID dashboard — no pencil, no inline editor (the
   *  rename lives on the dashboard). Guests (incl. Big Kid and the loading /
   *  kill-switch states) keep the local display-name editor unchanged.
   *  Home-modernize round (owner, supersedes M102b): a SIGNED-OUT non-owner
   *  sees the GET THE FULL GAME module here instead; signing in lives on the
   *  top-right LOG IN button. */
  private buildProfile(save: SaveData): HTMLElement {
    // M123: the storefront strip shows STRICTLY for a resolved non-owner
    // (ownsGame === false — never null/loading, never an owner) and never in
    // Big Kid (the M83 rule: kids see no purchase CTAs; the parent buys).
    const showStore = this.account?.ownsGame === false && save.settings.difficulty !== 'bigkid';
    // Owner round (home modernize): SIGNED OUT, the corner sells the game —
    // one big GET THE FULL GAME module (the free Quick Race + the sale are the
    // two things a cold visitor should see). The Racer ID entry moved to the
    // top-right LOG IN button; CREATE RACER ID is gone from the title (the
    // same account screen still creates one, and buying asks for it).
    // A signed-out OWNER (the offline-grace cache) keeps the guest profile.
    if (this.account?.status === 'signedOut' && showStore) {
      return this.buildBuyStrip();
    }
    const points = save.lifetimePoints;
    const tier = skillTier(points);
    const next = nextSkillTier(points);
    // M100: signed in + synced → the SERVER's world rank; else the local ladder.
    const acct = this.account;
    const rank =
      acct?.status === 'signedIn' && acct.rank ? acct.rank : localLeaderboard(points).playerRank;

    const wrap = el('div', 'title-profile', document.createElement('div'));
    // Leaderboard hidden (config/features.ts): the block is shorter (no rank
    // column) and a tap opens the RACER ID screen — the natural home for
    // "who am I / what have I banked" while rankings are parked.
    if (!LEADERBOARD_ENABLED) wrap.classList.add('title-profile-norank');
    wrap.setAttribute('role', 'button');
    wrap.tabIndex = 0;
    const signedIn = acct?.status === 'signedIn';
    if (signedIn) {
      // Owner round (Racer ID dashboard): tap ANYWHERE → the dashboard, and a
      // real keyboard button too (Enter / Space).
      wrap.classList.add('title-profile-account');
      wrap.setAttribute('aria-label', 'Open your Racer ID');
      const open = (): void => {
        sfxUiTick();
        this.cb.onAccount();
      };
      wrap.addEventListener('click', open);
      wrap.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        open();
      });
    } else {
      wrap.addEventListener('click', () => {
        if (!this.editing) {
          sfxUiTick();
          if (LEADERBOARD_ENABLED) this.cb.onLeaderboard();
          else this.cb.onAccount();
        }
      });
    }

    const avatar = el('span', 'tp-avatar', wrap);
    avatar.innerHTML = HELMET_SVG;
    avatar.style.setProperty('--tier-accent', tier.color);

    const main = el('span', 'tp-main', wrap);
    const nameRow = el('span', 'tp-name-row', main);
    const name = el('span', 'tp-name', nameRow);
    // M103: signed in, the SERVER username is the identity — the device-local
    // nickname can be stale (another account's, a guest's). Guests show theirs.
    const shown = (signedIn && acct.nickname) || save.nickname || 'RACER';
    name.textContent = shown.toUpperCase();
    if (signedIn) {
      // Owner round (Racer ID dashboard): the name is plain text here — the
      // tap belongs to the whole module (renames happen on the dashboard).
      nameRow.classList.add('tp-name-row-static');
    } else {
      nameRow.addEventListener('click', (e) => {
        e.stopPropagation();
        this.startNameEdit(nameRow, save);
      });
      const pencil = el('span', 'tp-edit', nameRow);
      pencil.innerHTML = PENCIL_SVG;
    }

    const tierRow = el('span', 'tp-tier-row', main);
    const badge = el('span', 'lb-tier lb-tier-mini', tierRow);
    badge.textContent = tier.name;
    badge.style.setProperty('--tier-accent', tier.color);
    // M131 (owner rule): the two pools are XP and SP and ALWAYS look
    // different. XP (gold star) only ever grows — rank, unlocks; it is drawn
    // as PROGRESS (the bar below). SP (green coin) is what the shop spends —
    // a balance; buying something visibly moves it.
    const xp = el('span', 'tp-xp', tierRow);
    const xpIc = el('span', 'tp-cur-ic', xp);
    xpIc.innerHTML = XP_STAR_SVG;
    xp.append(`${fmt(points)} XP`);
    const sp = el('span', 'tp-bank', tierRow);
    const spIc = el('span', 'tp-cur-ic', sp);
    spIc.innerHTML = SP_COIN_SVG;
    sp.append(`${fmt(spBalance(save))} SP`);

    // Progress toward the next rank (LEGEND caps out full) — an XP read.
    const span = next ? next.min - tier.min : 1;
    const pct = next ? Math.min(100, Math.max(3, ((points - tier.min) / span) * 100)) : 100;
    const bar = el('span', 'tp-bar', main);
    const fill = el('span', 'tp-bar-fill', bar);
    fill.style.width = `${pct}%`;
    el('span', 'tp-bar-note', main).textContent = next
      ? `${fmt(next.min - points)} XP TO ${next.name}`
      : `TOP RANK · ${SKILL_TIERS[SKILL_TIERS.length - 1].name}`;

    if (LEADERBOARD_ENABLED) {
      const rankCol = el('span', 'tp-rank', wrap);
      el('span', 'tp-ranknum', rankCol).textContent = `RANK #${rank}`;
      el('span', 'tp-ranksub', rankCol).textContent = 'LEADERBOARD ›';
    }

    // M123: a SIGNED-IN resolved non-owner (server owns_game=false — e.g. a
    // refunded player, or an account made before buying) keeps the full Racer
    // profile AND gets the storefront strip above it. Owners and unresolved
    // states return the plain profile — byte-identical to today.
    if (showStore) {
      const col = el('div', 'tp-col', document.createElement('div'));
      col.appendChild(this.buildBuyStrip());
      col.appendChild(wrap);
      return col;
    }
    return wrap;
  }

  /** The buy pill (owner reference, 2026-10-02): crown · BUY THE GAME ·
   *  price · chevron, with the one-purchase promise in small caps under it.
   *  Compact on purpose — the hero trophy on the stage is the emotional
   *  sell; this is the clear, priced action. Opens the paywall. */
  private buildBuyStrip(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'tp-buywrap';
    const price = this.account?.gamePrice ?? '$9.99';
    const cta = el('button', 'tp-buy', wrap);
    cta.type = 'button';
    cta.setAttribute('aria-label', `${BUY_GAME_LABEL}, ${price}`);
    el('span', 'tpb-ic', cta).innerHTML = CROWN_SVG;
    el('span', 'tpb-label', cta).textContent = BUY_GAME_LABEL;
    el('span', 'tpb-price', cta).textContent = price;
    el('span', 'tpb-chev', cta).innerHTML = CHEV_SVG;
    el('span', 'tpb-sheen', cta);
    el('div', 'tp-buy-note', wrap).textContent =
      'ONE-TIME PURCHASE · NO SUBSCRIPTION · FREE UPDATES';
    cta.addEventListener('click', () => {
      sfxUiTick();
      this.cb.onGetGame?.();
    });
    return wrap;
  }

  /** Swap the name label for an inline input; Enter/blur commits, Esc cancels.
   *  The loose LOCAL display name of a player who is not signed in (guests,
   *  Big Kid, the loading / kill-switch states). Owner round (Racer ID
   *  dashboard): a signed-in username is renamed on the dashboard instead —
   *  the M101 claim-in-place path that lived here is gone. */
  private startNameEdit(nameRow: HTMLElement, save: SaveData): void {
    if (this.editing) return;
    this.editing = true;
    const current = save.nickname || '';
    nameRow.replaceChildren();
    const input = document.createElement('input');
    input.className = 'tp-input';
    input.maxLength = NICKNAME_MAX;
    input.value = current;
    input.placeholder = 'RACER';
    input.autocomplete = 'off';
    input.spellcheck = false;

    /** Rebuild the label row. */
    const finish = (label: string): void => {
      nameRow.replaceChildren();
      el('span', 'tp-name', nameRow).textContent = label;
      const pencil = el('span', 'tp-edit', nameRow);
      pencil.innerHTML = PENCIL_SVG;
    };

    // The menus listen for game keys — typing must never steer the attract cam.
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') input.blur();
      if (e.key === 'Escape') {
        input.value = current;
        input.blur();
      }
    });
    input.addEventListener('blur', () => {
      this.editing = false;
      const name = sanitizeNickname(input.value);
      if (name !== current) {
        save.nickname = name; // keep the local view current pre-rebuild
        this.cb.onNickname(name);
      }
      finish((name || 'RACER').toUpperCase());
    });
    nameRow.appendChild(input);
    input.focus();
    input.select();
  }

  /** The hero card: tour kicker · WORLD TOUR · next stop, with the next city's
   *  silhouette art panel on the right (accent sky + neon horizon). */
  private buildHeroCard(save: SaveData): HTMLElement {
    const midTour = !!save.tour && !isTourComplete(save) && save.tour.results.length > 0;
    // M87: mid-tour the NEXT STOP comes from THIS tour's rolled order; a fresh
    // start doesn't know its order yet (rolled at START TOUR) — so the cover
    // is London's skyline (owner reference: a postcard skyline on the card).
    const city = midTour
      ? tourCityAt(save, tourCityIndex(save) ?? 0)
      : (CITIES.find((c) => c.id === 'london') ?? CITIES[0]);

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'title-hero';
    card.style.setProperty('--hero-accent', cssColor(city.theme.roadLine));
    card.style.setProperty('--stag', '0');
    card.addEventListener('click', () => {
      sfxUiTick();
      if (midTour) this.cb.onContinueTour();
      else this.cb.onStartTour();
    });

    const art = el('span', 'th-art', card);
    const sil = el('span', 'th-sil', art);
    sil.innerHTML = citySilhouette(city.id);
    // The REAL photo (the M82 city-card pattern — the procedural skyline stays
    // underneath as the fallback; NOT loading=lazy, the M82 gotcha). Owner
    // reference (2026-10-02): every state wears one, not just CONTINUE.
    const photo = document.createElement('img');
    photo.className = 'th-photo';
    photo.alt = '';
    photo.addEventListener('error', () => photo.remove());
    photo.src = `${import.meta.env.BASE_URL}cities/${city.id}.webp`;
    art.appendChild(photo);

    // Owner round (2026-10-02): the card is the WORLD TOUR CUP — title + one
    // status line (the reference layout); the old kicker row folds into it.
    const text = el('span', 'th-text', card);
    el('span', 'th-title', text).textContent = 'WORLD TOUR CUP';
    el('span', 'th-sub', text).textContent = midTour
      ? `CONTINUE · NEXT STOP: ${city.name.toUpperCase()}`
      : isTourComplete(save)
        ? `NEW TOUR · ${CITIES.length} CITIES`
        : `${CITIES.length} CITIES · 1 CHAMPION`;
    el('span', 'th-chev', card).innerHTML = CHEV_SVG;
    return card;
  }

  /** The mute button's face tracks the persisted state (M88). */
  private paintMute(b: HTMLButtonElement, muted: boolean): void {
    b.innerHTML = muted ? SPEAKER_MUTED_SVG : SPEAKER_SVG;
    b.classList.toggle('muted', muted);
    const label = muted ? 'Unmute audio' : 'Mute audio';
    b.setAttribute('aria-label', label);
    b.title = label;
  }

  /** Restarting a live tour wipes its progress — make it a two-tap confirm. */
  private addRestartButton(label: string, parent: HTMLElement): void {
    // Restarting wipes the live tour — the quiet destructive red (M92 rule).
    const b = btn(label, 'btn btn-danger-ghost title-ghost', parent, () => {
      if (b.classList.contains('confirming')) {
        this.clearConfirm();
        this.cb.onStartTour();
        return;
      }
      b.classList.add('confirming');
      b.textContent = 'RESET PROGRESS — SURE?';
      this.confirmTimer = window.setTimeout(() => {
        b.classList.remove('confirming');
        b.textContent = label;
        this.confirmTimer = null;
      }, RESET_CONFIRM_SECONDS * 1000);
    });
  }

  private clearConfirm(): void {
    if (this.confirmTimer !== null) {
      window.clearTimeout(this.confirmTimer);
      this.confirmTimer = null;
    }
  }

  hide(): void {
    this.clearConfirm();
    if (!this.visible) return;
    this.visible = false;
    this.root.style.display = 'none';
    this.root.classList.remove('intro', 'warm');
    this.stage.hide();
    this.cb.onVisibleChange(false);
  }

  dispose(): void {
    this.clearConfirm();
    this.unsubViewport?.();
    this.stage.dispose();
    this.root.remove();
  }
}
